// layouts/autenticacion/usuarios/editar-usuario.jsx
import { useEffect, useMemo, useState } from "react";
import { Grid, Card, Alert, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import PermissionUser from "../components/PermissionUser";
import FormUser from "../components/FormUser";

import api from "services/api";
import { getUser, updateUser } from "layouts/autenticacion/usuarios/services/users";

/* ---------- helpers permisos ---------- */
const MODULE_MAP_API_TO_UI = { user: "usuarios", award: "reconocimientos", reportes: "reportes" };
const MODULE_MAP_UI_TO_API = {
  usuarios: "user",
  reconocimientos: "award",
  reportes: "reportes",
  dashboard: "dashboard",
  sellos: "sellos",
  generos: "generos",
  grupos: "grupos",
  nacionalidades: "nacionalidades",
  certificaciones: "certificaciones",
};

const ACTIONS = ["read", "create", "update", "delete"];

const stripHtml = (s) =>
  String(s || "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const safeString = (x) => {
  try {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (x?.message && typeof x.message === "string") return x.message;
    if (x?.instancePath || x?.schemaPath || x?.keyword) {
      return `${x.instancePath || ""} ${x.message || ""}`.trim() || JSON.stringify(x);
    }
    return JSON.stringify(x);
  } catch {
    return String(x);
  }
};

const unwrapList = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.page)
    ? data.page
    : Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.results)
    ? data.results
    : [];

const getTotal = (data, fallback = 0) =>
  Number(data?.total ?? data?.count ?? data?.totalRows ?? fallback);

/**
 * Carga todos los grupos para selectors.
 * Intenta usar paginación server-side con:
 * - __limit
 * - __offset
 * - __sort
 *
 * Si el backend no soporta esos params, hace fallback a /group normal.
 */
async function fetchAllGroups() {
  const pageSize = 100;
  let offset = 0;
  let all = [];
  let total = null;

  try {
    // máximo de seguridad para evitar loops infinitos
    for (let i = 0; i < 50; i += 1) {
      const { data } = await api.get("/group", {
        params: {
          __limit: pageSize,
          __offset: offset,
          __sort: { name: 1 },
        },
      });

      const page = unwrapList(data);
      all = [...all, ...page];

      total = getTotal(data, all.length);

      if (!page.length) break;
      if (all.length >= total) break;
      if (page.length < pageSize) break;

      offset += pageSize;
    }

    return all;
  } catch (e) {
    const msg = e?.response?.data?.message || e.message || "";

    const paginationNotSupported =
      String(msg).includes("property '__limit' is not defined") ||
      String(msg).includes("property '__offset' is not defined") ||
      String(msg).includes("property '__sort' is not defined") ||
      e?.response?.status === 400 ||
      e?.response?.status === 500;

    if (!paginationNotSupported) throw e;

    // Fallback: comportamiento anterior
    const { data } = await api.get("/group");
    return unwrapList(data);
  }
}

const apiPermissionsToKeyArray = (permissions = {}) => {
  const keys = [];

  Object.entries(permissions || {}).forEach(([apiModule, perms]) => {
    const uiModule = MODULE_MAP_API_TO_UI[apiModule] || apiModule;

    ACTIONS.forEach((a) => {
      if (perms?.[a]) keys.push(`${uiModule}.${a}`);
    });
  });

  return keys;
};

const keyArrayToApiPermissions = (keys = []) => {
  const out = {};

  keys.forEach((k) => {
    if (typeof k !== "string" || !k.includes(".")) return;

    const [uiMod, action] = k.split(".");
    const apiMod = MODULE_MAP_UI_TO_API[uiMod] || uiMod;

    if (!ACTIONS.includes(action)) return;

    if (!out[apiMod]) {
      out[apiMod] = {
        read: false,
        create: false,
        update: false,
        delete: false,
      };
    }

    out[apiMod][action] = true;

    if (action !== "read") out[apiMod].read = true;
  });

  Object.keys(out).forEach((m) => {
    const p = out[m];

    out[m] = {
      read: !!p.read,
      create: !!p.create,
      update: !!p.update,
      delete: !!p.delete,
    };
  });

  return out;
};

/* ---------- Panel de permisos enlazado a la API ---------- */
function PermissionUserPanel({ userId }) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [permKeys, setPermKeys] = useState([]);
  const [originalKeys, setOriginalKeys] = useState([]);

  const dirty = useMemo(() => {
    if (permKeys.length !== originalKeys.length) return true;

    const a = new Set(permKeys);
    return !originalKeys.every((k) => a.has(k));
  }, [permKeys, originalKeys]);

  useEffect(() => {
    let cancel = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const user = await getUser(userId);
        const keys = apiPermissionsToKeyArray(user?.permissions || {});

        if (!cancel) {
          setPermKeys(keys);
          setOriginalKeys(keys);
        }
      } catch (e) {
        if (!cancel) {
          setError(
            e?.response?.data
              ? stripHtml(e.response.data)
              : e?.message || "Error al cargar permisos"
          );
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      const permissions = keyArrayToApiPermissions(permKeys);

      await updateUser(userId, { permissions });

      setOriginalKeys(permKeys);

      navigate("/autenticacion/usuarios", {
        replace: true,
        state: { refresh: Date.now() },
      });
    } catch (e) {
      setError(
        e?.response?.data
          ? stripHtml(e.response.data)
          : e?.message || "Error al guardar permisos"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SoftTypography variant="h6">Permisos</SoftTypography>

        <SoftBox display="flex" gap={1}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={() => setPermKeys(originalKeys)}
            disabled={!dirty || saving}
          >
            Restablecer
          </SoftButton>

          <SoftButton
            variant="outlined"
            color="error"
            onClick={() => setPermKeys([])}
            disabled={saving}
          >
            Quitar todos
          </SoftButton>

          <SoftButton
            color="dark"
            variant="gradient"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? "Guardando…" : "Guardar"}
          </SoftButton>
        </SoftBox>
      </SoftBox>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {safeString(error)}
        </Alert>
      )}

      {loading ? (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <SoftTypography>Cargando permisos…</SoftTypography>
        </SoftBox>
      ) : (
        <PermissionUser
          value={permKeys}
          onChange={(next) => setPermKeys(Array.isArray(next) ? next : [])}
        />
      )}
    </Card>
  );
}

/* ---------- Página ---------- */
export default function EditarUsuario() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Usuario
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [errUser, setErrUser] = useState("");

  // Grupos
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [errGroups, setErrGroups] = useState("");

  // Guardado y errores del form
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState([]);

  // Cargar usuario
  useEffect(() => {
    let cancel = false;

    (async () => {
      setLoadingUser(true);
      setErrUser("");

      try {
        const u = await getUser(id);

        if (!cancel) setUser(u);
      } catch (e) {
        if (!cancel) {
          setErrUser(
            e?.response?.data
              ? stripHtml(e.response.data)
              : e?.message || "No se pudo cargar el usuario"
          );
        }
      } finally {
        if (!cancel) setLoadingUser(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [id]);

  // Cargar todos los grupos para el selector
  useEffect(() => {
    let cancel = false;

    (async () => {
      setLoadingGroups(true);
      setErrGroups("");

      try {
        const list = await fetchAllGroups();

        if (!cancel) {
          setGroups(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        if (!cancel) {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            "No se pudieron cargar los grupos";

          setErrGroups(safeString(msg));
          setGroups([]);
        }

        console.error("[EditarUsuario] error cargando grupos:", e);
      } finally {
        if (!cancel) setLoadingGroups(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const handleSaveUser = async (payload) => {
    setFormErrors([]);

    try {
      setSaving(true);

      await updateUser(id, payload);

      navigate("/autenticacion/usuarios", { replace: true });
    } catch (e) {
      const msg = e?.response?.data || e?.message || "No se pudo guardar";
      setFormErrors([safeString(msg)]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox mt={5} mb={3}>
        <Grid container spacing={3}>
          {/* Izquierda: datos del usuario */}
          <Grid item xs={12} md={6}>
            {errUser ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {safeString(errUser)}
              </Alert>
            ) : (
              <>
                {errGroups && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {errGroups}
                  </Alert>
                )}

                <FormUser
                  key={user ? user.id ?? user._id ?? "edit-user" : "loading-user"}
                  mode="edit"
                  user={user}
                  initialUser={user}
                  loading={loadingUser || loadingGroups}
                  groups={groups}
                  saving={saving}
                  errors={formErrors}
                  onSave={handleSaveUser}
                  onCancel={() => navigate("/autenticacion/usuarios")}
                />
              </>
            )}
          </Grid>

          {/* Derecha: permisos */}
          <Grid item xs={12} md={6}>
            <PermissionUserPanel userId={id} />
          </Grid>
        </Grid>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}